import { Observable, of, pipe } from 'rxjs';
import { distinctUntilChanged, filter, first, map, mapTo, skipWhile, startWith, switchMap } from 'rxjs/operators';

import type { Actions } from '~/actions';
import type { AppStore } from '~/types/main.types';

import { ofType } from '@mode/legacy/util-domain';
import { RouterActionType } from '~/actions/router-actions';
import { routerNameSelector } from '~/reducers/selectors';
import { isExploringVizOrExplorationRoute } from '~/lib/router-helpers';
import { TempActions } from '@mode/charts/domain';
import { SwitchViewActions } from '@mode/switch-views/contract';

export function onExploringStateChanged(actions$: Observable<Actions>, store: AppStore) {
  return actions$.pipe(ofType(RouterActionType.UpdateRoute)).pipe(
    // Kick off the pipes in case we init after the UpdateRoute Action
    startWith({}),
    // Get current route
    switchMap(() => store.selectNoEmpty(routerNameSelector)),
    // Determine if we're exploring or not
    map(isExploringVizOrExplorationRoute),
    // Skip initial states when not-exploring
    skipWhile((exploring) => !exploring),
    // Don't emit more than once per state change
    distinctUntilChanged(),
    switchMap((isExploring) => {
      // Not exploring, no further observation is needed
      if (!isExploring) return of(false);
      // Is exploring, need to fire this one right off the bat as well as
      // for subsequent data loading events but _only while exploring_
      return actions$.pipe(
        // Listen for data loading actions
        ofType(
          SwitchViewActions.pollingSwitchViewCompleted,
          SwitchViewActions.loadGfDataViews,
          TempActions.loadExploration
        ),
        // Make sure we at least get this once in the event that the API requests
        // have already returned
        startWith(true),
        mapTo(true)
      );
    })
  );
}

export function filterIsExploring<T>(forExploring: boolean, store: AppStore) {
  return pipe(
    switchMap((value: T) =>
      store
        .selectNoEmpty(routerNameSelector)
        .pipe(first())
        .pipe(
          map(isExploringVizOrExplorationRoute),
          filter((isExploring) => isExploring === forExploring),
          map(() => value)
        )
    )
  );
}
